import { forwardRef, useCallback } from 'react'
import { Slider } from '~/shadcn/ui/slider'
import { cn } from '~/shadcn/lib/utils'

type SliderStyledProps = Omit<
  React.ComponentProps<typeof Slider>,
  'onChange' | 'onChangeCommitted'
> & {
  trackcoloroverride?: string
  thumbcoloroverride?: string
  colorScheme?: 'light' | 'dark'
  // Compatibility props for MUI API
  onChange?: (event: Event, value: number | number[]) => void
  onChangeCommitted?: (event: Event, value: number | number[]) => void
}

export const SliderStyled = forwardRef<
  React.ComponentRef<typeof Slider>,
  SliderStyledProps
>(
  (
    {
      className,
      colorScheme = 'dark',
      value,
      onChange,
      onChangeCommitted,
      onValueChange,
      onValueCommit,
      ...props
    },
    ref
  ) => {
    // Convert single value to array for Radix UI
    const valueArray = (() => {
      if (value === undefined) return undefined
      return Array.isArray(value) ? value : [value]
    })()

    const isDark = colorScheme === 'dark'

    // Event Handlers
    const handleValueChange = useCallback(
      (values: number[]) => {
        const singleValue = values[0]
        onValueChange?.(values)
        if (onChange) {
          const syntheticEvent = {
            target: { value: singleValue },
          } as unknown as Event
          onChange(syntheticEvent, singleValue)
        }
      },
      [onChange, onValueChange]
    )

    const handleValueCommit = useCallback(
      (values: number[]) => {
        const singleValue = values[0]
        onValueCommit?.(values)
        if (onChangeCommitted) {
          const syntheticEvent = {
            target: { value: singleValue },
          } as unknown as Event
          onChangeCommitted(syntheticEvent, singleValue)
        }
      },
      [onChangeCommitted, onValueCommit]
    )

    return (
      <Slider
        ref={ref}
        className={cn(
          `${isDark ? "**:data-[slot='slider-track']:bg-border-strong/20" : "**:data-[slot='slider-track']:bg-muted"}`,
          `${isDark ? "**:data-[slot='slider-range']:bg-green/70" : "**:data-[slot='slider-range']:bg-primary"}`,
          className
        )}
        value={valueArray}
        onValueChange={handleValueChange}
        onValueCommit={handleValueCommit}
        {...props}
      />
    )
  }
)

SliderStyled.displayName = 'SliderStyled'
